//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import OutletImportHelper from './OutletImportHelper';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';
import PjpImportHelper from 'common/entity/helpers/PjpImportHelper';

const addTimeSlots = defaultColumns => {
    const slotColumns = [];
    Object.values(DayOfWeekType).forEach(dayOfWeek => {
        slotColumns.push({
            id: dayOfWeek,
            titleKey: 'masterOutletProperties.timeSlots',
            titleId: dayOfWeek,
            accessor: ['timeSlots', dayOfWeek],
            templateValue: 'masterImportTemplateValues.masterOutlet.timeSlots',
            processCell: OutletImportHelper.processTimeSlots,
            validateCell: OutletImportHelper.validateEntityTimeSlots,
            serializeCell: OutletImportHelper.serializeMultiDaySlot,
            getModelFieldAccessor: OutletImportHelper.getTimeSlotsAccessor
        });
    });
    const columns = [...defaultColumns, ...slotColumns];
    return columns;
};

export const MAX_DURATION_FREQUENCY_IN_WEEKS = 13;

const OUTLET_IMPORT_FORMAT: Array<TableColumnTypes> = addTimeSlots([
    {
        titleKey: 'masterOutletProperties.outletId',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterOutlet.outletId'
    },
    {
        titleKey: 'masterOutletProperties.outletCode',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterOutlet.outletCode'
    },
    {
        titleKey: 'masterOutletProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterOutlet.name'
    },
    {
        titleKey: 'masterOutletProperties.teamId',
        id: 'teams',
        required: true,
        templateValue: 'masterImportTemplateValues.masterOutlet.team',
        // allowing only single team in outlets
        processCell: OutletImportHelper.processTeam,
        validateCell: OutletImportHelper.validateTeamIds,
        serializeCell: OutletImportHelper.serializeTeams
    },
    {
        titleKey: 'masterOutletProperties.transactionDuration',
        id: 'transactionDuration',
        templateValue:
            'masterImportTemplateValues.masterOutlet.transactionDuration',
        processCell: OutletImportHelper.processMinutesToSeconds
    },
    {
        titleKey: 'masterOutletProperties.planningDuration',
        id: 'planningDuration',
        accessor: ['visitFrequencyInfo', 'duration'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.planningDuration',
        processCell: ({ cellValue }) => {
            return NumberUtils.isPositiveNumber(cellValue)
                ? cellValue * 7
                : cellValue;
        },
        validateCell: ({ entity, t }) => {
            const { visitFrequencyInfo: { duration } = null } = entity;
            const frequencyInWeeks = duration ? duration / 7 : null;
            return frequencyInWeeks &&
                (frequencyInWeeks < 1 ||
                    frequencyInWeeks > MAX_DURATION_FREQUENCY_IN_WEEKS)
                ? t(
                      'validations:outletValidationErrors.invalidPlanningDuration'
                  )
                : false;
        }
    },
    {
        titleKey: 'masterOutletProperties.frequencyRepeatCount',
        id: 'repeatCount',
        accessor: ['visitFrequencyInfo', 'repeatCount'],
        templateValue: 'masterImportTemplateValues.masterOutlet.repeatCount'
    },
    {
        titleKey: 'masterOutletProperties.spaceVisitsEvenly',
        id: 'spaceVisitsEvenly',
        accessor: ['visitFrequencyInfo', 'spaceVisitsEvenly'],

        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.locationId',
        id: 'locationId',
        accessor: ['locationInfo', 'locationId', 'locationId'],
        getModelFieldAccessor: OutletImportHelper.getLocationInfoAccessor,
        serializeCell: OutletImportHelper.serializeLocationInfo,
        templateValue: 'masterImportTemplateValues.masterOutlet.locationId',
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateLocationIds,
            clientId
        }) => {
            OutletImportHelper.validateEntityIdAsync({
                validator: validateLocationIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterOutletProperties.newLocationId',
        id: 'newLocationId',
        accessor: ['locationInfo', 'location', 'id'],
        templateValue: 'masterImportTemplateValues.masterOutlet.locationId',
        getModelFieldAccessor: OutletImportHelper.getLocationInfoAccessor,
        serializeCell: OutletImportHelper.serializeLocationInfo,
        validateCell: OutletImportHelper.validateNewLocationId,
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateLocationIds,
            clientId
        }) => {
            OutletImportHelper.validateEntityIdAsync({
                validator: validateLocationIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header,
                checkIsNewId: true
            });
        }
    },
    {
        titleKey: 'masterOutletProperties.locationType',
        id: 'locationType',
        accessor: ['locationInfo', 'location', 'type'],
        required: true,
        templateValue: 'masterImportTemplateValues.masterOutlet.locationType',
        validateCell: OutletImportHelper.validateLocationType,
        processCell: OutletImportHelper.processUpperCaseId
    },
    {
        titleKey: 'masterOutletProperties.address.completePostalAddress',
        required: false,
        id: 'formattedAddress',
        accessor: ['locationInfo', 'location', 'address', 'formattedAddress'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.address.address',
        validateCell: OutletImportHelper.validateAddressFields
    },
    {
        titleKey: 'masterOutletProperties.address.locality',
        required: false,
        id: 'localityName',
        accessor: ['locationInfo', 'location', 'address', 'localityName'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.address.locality',
        validateCell: OutletImportHelper.validateAddressFields
    },
    {
        titleKey: 'masterOutletProperties.address.city',
        required: false,
        id: 'city',
        accessor: ['locationInfo', 'location', 'address', 'city'],
        templateValue: 'masterImportTemplateValues.masterOutlet.address.city',
        validateCell: OutletImportHelper.validateAddressFields
    },
    {
        titleKey: 'masterOutletProperties.address.state',
        required: false,
        id: 'state',
        accessor: ['locationInfo', 'location', 'address', 'state'],
        templateValue: 'masterImportTemplateValues.masterOutlet.address.state',
        validateCell: OutletImportHelper.validateAddressFields
    },
    {
        titleKey: 'masterOutletProperties.address.pincode',
        required: false,
        id: 'pinCode',
        accessor: ['locationInfo', 'location', 'address', 'pincode'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.address.pincode',
        validateCell: OutletImportHelper.validateAddressFields
    },
    {
        titleKey: 'masterOutletProperties.address.country',
        required: false,
        id: 'countryCode',
        accessor: ['locationInfo', 'location', 'address', 'countryCode'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.address.country',
        validateCell: OutletImportHelper.validateAddressFields
    },
    {
        titleKey: 'masterOutletProperties.latitude',
        required: false,
        id: 'lat',
        accessor: ['locationInfo', 'location', 'latLng', 'lat'],
        templateValue: 'masterImportTemplateValues.masterOutlet.latitude'
    },
    {
        titleKey: 'masterOutletProperties.longitude',
        required: false,
        id: 'lng',
        accessor: ['locationInfo', 'location', 'latLng', 'lng'],
        templateValue: 'masterImportTemplateValues.masterOutlet.longitude'
    },
    {
        titleKey: 'masterOutletProperties.currentRiderId',
        required: false,
        id: 'currentRiderId',
        accessor: ['historicalData', 'currentRiderId'],
        templateValue: 'masterImportTemplateValues.masterOutlet.riderId',
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateRiderIds,
            clientId
        }) => {
            OutletImportHelper.validateEntityIdAsync({
                validator: validateRiderIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterOutletProperties.currentPjpName',
        required: false,
        id: 'currentPjpName',
        accessor: ['historicalData', 'currentBeatId'],
        templateValue: 'masterImportTemplateValues.masterOutlet.currentPjpName'
    },
    {
        titleKey: 'masterOutletProperties.currentPjpDay',
        id: 'currentPjpDay',
        accessor: ['historicalData', 'currentPjpDayInfo', 'dayOfWeek'],
        templateValue: 'masterImportTemplateValues.masterOutlet.currentPjpDay',
        processCell: ({ cellValue }) => cellValue?.toUpperCase(),
        validateCell: OutletImportHelper.validateDayOfWeek
    },
    {
        titleKey: 'masterOutletProperties.currentPjpWeek',
        id: 'currentPjpWeek',
        accessor: ['historicalData', 'currentPjpDayInfo', 'week'],
        templateValue: 'masterImportTemplateValues.masterOutlet.currentPjpWeek',
        processCell: ({ cellValue }) => cellValue?.toUpperCase(),
        validateCell: OutletImportHelper.validateWeek
    },
    {
        titleKey: 'masterOutletProperties.currentPjpType',
        id: 'currentPjpType',
        customFieldsKey: 'currentPjpType',
        accessor: ['historicalData', 'currentPjpType'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.customFieldsValue'
    },
    {
        titleKey: 'masterOutletProperties.zoneId',
        id: 'zoneId',
        accessor: ['zoneId'],
        templateValue: 'masterImportTemplateValues.masterOutlet.zone'
    },
    {
        titleKey: 'masterOutletProperties.salesCategoryId',
        id: 'salesCategoryId',
        required: true,
        accessor: ['salesCategoryId', 'salesCategoryId'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.salesCategories',
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateSalesCategoryIds,
            clientId
        }) => {
            OutletImportHelper.validateEntityIdAsync({
                validator: validateSalesCategoryIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        },
        getModelFieldAccessor: OutletImportHelper.getSalesCategoryIdAccessor,
        serializeCell: OutletImportHelper.serializeSalesCategoryId
    },
    {
        titleKey: 'masterOutletProperties.channels',
        id: 'channels',
        accessor: ['channels'],
        customFieldsKey: 'channels',
        templateValue:
            'masterImportTemplateValues.masterOutlet.customFieldsValue',
        validateCell: PjpImportHelper.validateChannels,
        processCell: PjpImportHelper.processChannelPreferences,
        serializeCell: PjpImportHelper.serializeChannels
    },
    {
        titleKey: 'masterOutletProperties.serviceMix',
        id: 'serviceMix',
        customFieldsKey: 'serviceMix',
        templateValue:
            'masterImportTemplateValues.masterOutlet.customFieldsValue'
    },
    {
        titleKey: 'masterOutletProperties.revenueGeneratedInMonths',
        id: 'recentRevenue',
        accessor: ['historicalData', 'recentRevenue', 'amount', 'amount'],
        templateValue:
            'masterImportTemplateValues.masterOutlet.revenueGenerated'
    },
    {
        titleKey: 'masterOutletProperties.skuSales',
        id: 'averageSkusSold',
        accessor: ['historicalData', 'averageSkusSold'],
        templateValue: 'masterImportTemplateValues.masterOutlet.skuSales'
    },
    {
        titleKey: 'masterOutletProperties.shouldKeep',
        id: 'shouldKeep',
        accessor: ['historicalData', 'shouldKeep'],
        templateValue: 'masterImportTemplateValues.masterOutlet.keepRemove',
        validateCell: OutletImportHelper.getBooleanValidator({
            positiveLabelKey:
                'masterImportTemplateValues.masterOutlet.booleanValues.keep',
            negativeLabelKey:
                'masterImportTemplateValues.masterOutlet.booleanValues.remove'
        }),
        processCell: OutletImportHelper.getBooleanProcessor({
            positiveLabelKey:
                'masterImportTemplateValues.masterOutlet.booleanValues.keep',
            negativeLabelKey:
                'masterImportTemplateValues.masterOutlet.booleanValues.remove'
        })
    },
    {
        titleKey: 'masterOutletProperties.saleMadeRecentlyInMonths',
        id: 'saleMadeRecently',
        accessor: ['historicalData', 'saleMadeRecently'],
        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.duplicateParty',
        id: 'duplicateParty',
        accessor: ['historicalData', 'duplicateParty'],
        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.isNew',
        id: 'isNew',
        accessor: ['historicalData', 'isNew'],
        customFieldsKey: 'isNew',
        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.isFrozenToRider',
        id: 'isFrozenToRider',
        accessor: ['historicalData', 'isFrozenToRider'],
        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.frozenRiderId',
        id: 'frozenRiderId',
        accessor: ['historicalData', 'frozenRiderId'],
        templateValue: 'masterImportTemplateValues.masterOutlet.riderId',
        validateCell: ({ entity, t }) => {
            const {
                historicalData: { isFrozenToRider, frozenRiderId } = {}
            } = entity;
            return isFrozenToRider && DataUtils.isEmpty(frozenRiderId)
                ? t(
                      'validations:outletValidationErrors.historicalData.emptyFrozenRiderId'
                  )
                : false;
        },
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateRiderIds,
            clientId
        }) => {
            OutletImportHelper.validateEntityIdAsync({
                validator: validateRiderIds,
                clientId,
                onSuccess,
                onFailure,
                entity,
                header
            });
        }
    },
    {
        titleKey: 'masterOutletProperties.isFrozenToBeat',
        id: 'isFrozenToBeat',
        accessor: ['historicalData', 'isFrozenToBeat'],
        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.frozenBeat',
        id: 'frozenBeat',
        accessor: ['historicalData', 'frozenTourId'],
        templateValue: 'masterImportTemplateValues.masterOutlet.frozenBeat',
        validateCell: ({ entity, t }) => {
            const {
                historicalData: { isFrozenToBeat, frozenTourId } = {}
            } = entity;
            return isFrozenToBeat && DataUtils.isEmpty(frozenTourId)
                ? t(
                      'validations:outletValidationErrors.historicalData.emptyFrozenTourId'
                  )
                : false;
        }
    },
    {
        titleKey: 'masterOutletProperties.serviceBySameRider',
        id: 'serviceBySameRider',
        accessor: ['historicalData', 'serviceBySameRider'],
        templateValue: 'masterImportTemplateValues.masterOutlet.booleanValue',
        validateCell: OutletImportHelper.getBooleanValidator(),
        processCell: OutletImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterOutletProperties.mandatoryDays',
        id: 'mandatoryDays',
        templateValue: 'masterImportTemplateValues.masterOutlet.mandatoryDays',
        processCell: OutletImportHelper.processMandatoryDays,
        validateCell: OutletImportHelper.validateDaysOfWeek,
        serializeCell: OutletImportHelper.serializeMandatoryDays
    },
    {
        titleKey: 'masterOutletProperties.holidays',
        id: 'holidays',
        accessor: ['holidays'],
        templateValue: 'masterImportTemplateValues.masterOutlet.holidays',
        processCell: OutletImportHelper.processHolidays,
        validateCell: OutletImportHelper.validateHolidays,
        serializeCell: OutletImportHelper.serializeHolidays
    },
    {
        titleKey: 'masterOutletProperties.volume',
        id: 'averageVolume',
        accessor: ['averageVolume'],
        templateValue: 'masterImportTemplateValues.masterOutlet.volume'
    }
]);

export default OUTLET_IMPORT_FORMAT;
